import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'

import apis from 'browser/app/models/apis'
import { ISelectProps, Select } from 'browser/components/atomic-elements/atoms/select'
import type {
  SelectComponentProps,
  SelectStyleProps,
} from 'browser/components/atomic-elements/atoms/select/select/interface'
// tslint:disable-next-line:max-line-length
import { EntityAssociationsSheet } from 'browser/components/atomic-elements/organisms/entity/entity-associations/entity-associations-sheet'
import { EntityFormSheet, EntityFormSheetMode } from 'browser/components/atomic-elements/organisms/entity/entity-form-sheet'
// tslint:disable-next-line:max-line-length
import { Entity } from 'shared-libs/models/entity'
import { ALL, Query } from 'shared-libs/models/query'
import { SheetContext } from '../sheet/sheet-manager'
import { Store } from 'shared-libs/models/store'
import { isUUIDValid } from 'shared-libs/helpers/utils'
import { ComponentsContext } from 'browser/contexts/components/components-context'
import { EntityMapper, ISimpleMapping, toIMapping } from 'shared-libs/models/entity-mapping'
import { VALUELESS_FILTERS } from 'shared-libs/models/local-adapter/filters'
import { evaluateExpression } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { creationSourceFromWeb, DEBOUNCE_TIMEOUT } from 'browser/app/utils/utils'
import { components } from 'react-select'
import { JSONSchemaResolver } from 'shared-libs/resolvers/json-schema-resolver'
import { ConfirmationModal } from '../../organisms/confirmation-modal'
import { EntityPreviewCard } from '../../organisms/entity/entity-preview-card/entity-preview-card'
import ComponentsMap from 'browser/components'

const DEFAULT_ORDERS = [
  {
    path: 'precomputation.displayName',
    type: 'ascending',
  },
]

export interface Filter {
  type: string
  path: string
  value?: any
  values?: any[]
}

/**
 * @uiComponent
 */
export interface IEntitySelectProps extends ISelectProps {
  addInflationSessionId?: (id: string) => void
  edgeToOptionCreator?: (edge: any, schema: any) => any
  entityToEdgeCreator?: (entity: any, schema: any, denormalizedProperties: any[]) => any
  removeInflationSessionId?: (id: string) => void
  onOptionsChange?: (...args: any[]) => {}

  defaultNewEntityValue?: any
  defaultNewEntityInputValuePath?: string
  denormalizedProperties?: any[]
  entity?: any
  entityType: string
  filter?: Filter // deprecated
  filters?: Filter[]
  orders?: any[]
  querySize?: number
  isCreatable?: boolean
  newOptionAssociationProps?: any
  newOptionMappings?: ISimpleMapping[]
  /**
   * @deprecated 5/15/2026 - Keep for backwards compatibility for old schemas
   * Controls whether to make the selected value clickable to open the entity details.
   * Replaced by `openEntityDetailsOnClick`
   */
  showLinkIcon?: boolean
  showInlinePreview?: boolean
  /**
   * Open entity details on click.
   */
  openEntityDetailsOnClick?: boolean
  /**
   * Open entity details when the selection changes. Useful for cases where immediate editing or review is common,
   * such as when selecting a trailer to inbound.
   */
  openEntityDetailsOnChange?: boolean
  openOverlay?: any
  entitySheetMode?: EntityFormSheetMode
  optionHeight?: number
  returnValueAsEdge?: boolean
  queryPath?: string
  queryPaths?: (string | { path: string; boost: number })[]
  shouldPreloadEntity?: boolean

  runQueryOnPropsUpdate?: boolean
  entityCreateSchema?: string
  entityCreateState?: any
  entityDetailSchema?: string
  entityDetailState?: any
  entityEditSchema?: string
  entityEditState?: any
  selectedValueCardSchema?: string
  optionValueCardSchema?: string
  preloadOptions?: boolean

  transformQueryFn?: (input: string) => string | string[]
  queryType?: string
  confirmChangeFn?: (currentEntity: Entity | undefined, option: any) => boolean | Promise<boolean>

  // Multi-select options (entity-select specific defaults/overrides for ISelectProps multi props)
  allowSelectAll?: boolean
  isItemClearable?: boolean
  groupOptionsFn?: (options: any[]) => Array<{ label: string; options: any[] }>
  groupOptionsFormula?: string
}

type IEntitySelectPropsWithDefaults = IEntitySelectProps &
  Required<
    Pick<
      IEntitySelectProps,
      'edgeToOptionCreator' | 'entityToEdgeCreator' | 'optionLabelPath' | 'optionValuePath'
    >
  >

interface IEntitySelectState {
  inputValue: string | null | undefined
  isLoading: boolean
  options: any[]
  value: any
  entity?: Entity
  menuIsOpen: boolean
}

class EntitySelect extends React.Component<IEntitySelectPropsWithDefaults, IEntitySelectState> {
  public static defaultProps: Partial<IEntitySelectProps> = {
    edgeToOptionCreator: defaultEdgeToOptionCreator,
    entityToEdgeCreator: defaultEntityToEdgeCreator,
    isCreatable: true,
    optionLabelPath: 'displayName',
    optionValuePath: 'uniqueId',
    returnValueAsEdge: true,
    showLinkIcon: true,
    openEntityDetailsOnClick: true,
    removeOptionText: '- Clear Selection -',
    entityCreateSchema: 'uiSchema.web.entityCreationSheet',
    entityDetailSchema: 'uiSchema.web.entityDetailSheet',
    entityEditSchema: 'uiSchema.web.entityEditSheet',
    selectedValueCardSchema: 'uiSchema.web.entitySelectDisplayCard',
    optionValueCardSchema: 'uiSchema.web.entitySelectOptionCard',
    entitySheetMode: EntityFormSheetMode.Edit,
    minMenuHeight: 300, // forces options above input when near the bottom of the screen
    isItemClearable: true,
  }

  static contextType = ComponentsContext
  context!: React.ContextType<typeof ComponentsContext>

  public collection: any
  private entitySchema: any
  private input: Select
  private store: Store
  private isOptionsLoaded: boolean
  private loadingPromise: any
  private _isCreatableMeta: boolean | undefined

  private schemaResolver: JSONSchemaResolver
  private resolvedSelectedValueCardSchema: any
  private resolvedOptionValueCardSchema: any

  constructor(props: IEntitySelectPropsWithDefaults) {
    super(props)
    this.store = apis.getStore()
    this.schemaResolver = new JSONSchemaResolver(apis)
    const { options, value, shouldPreloadEntity, selectedValueCardSchema, optionValueCardSchema, entityType } = props
    this.state = {
      inputValue: undefined,
      isLoading: false,
      options: options || [],
      value: undefined,
      menuIsOpen: false,
    }
    this.isOptionsLoaded = false
    this.store.resolveSchema(entityType).then((schema: Entity) => {
      this.entitySchema = schema
      this.resolvedSelectedValueCardSchema = this.schemaResolver.resolveSubschemaByPath(
        schema,
        selectedValueCardSchema
      )
      this.resolvedOptionValueCardSchema = this.schemaResolver.resolveSubschemaByPath(
        schema,
        optionValueCardSchema
      )
    })

    if (value?.entityId) {
      this.store.findRecordThrottled(value.entityId).then((entity) => {
        if (shouldPreloadEntity) {
          this.handleChange(entity.uniqueId, entity)
        }
        this.setState({ entity: entity })
      })
    }

    this.handleInputChanged = _.debounce(this.handleInputChangedOrig, DEBOUNCE_TIMEOUT)
  }

  public async componentDidMount() {
    this.entitySchema = await this.store.resolveSchema(this.props.entityType)
    this._isCreatableMeta = this.entitySchema.metadata.isSearchCreatableDefault

    if (!_.isEmpty(this.props.value)) {
      if (this.props.multi) {
        const edges = _.castArray(this.props.value).filter(Boolean)
        this.setState({
          value: edges.map((edge) => this.props.edgeToOptionCreator(edge, this.entitySchema)).filter(Boolean),
        })
      } else {
        this.setState({
          value: this.props.edgeToOptionCreator(this.props.value, this.entitySchema),
        })
      }
    }

    await this.initializeCollection(this.props)

    if (this.props.preloadOptions === true) {
      this.handleInputChangedOrig(null)
    }
  }

  private async initializeCollection(props: IEntitySelectPropsWithDefaults): Promise<any> {
    this.collection?.cancel()

    if (!this.store.getRecord(props.entityType)) {
      await this.store.findSchemas([props.entityType])
    }

    const query = new Query(apis).setEntityType(props.entityType).setOrders(DEFAULT_ORDERS)
    if (props.querySize) {
      query.setSize(props.querySize)
    }
    if (props.queryPaths) {
      query.setQueryPaths(props.queryPaths)
    } else {
      query.setQueryPath(props.queryPath)
    }
    if (props.queryType) {
      query.setQueryType(props.queryType)
    }
    if (props.filter) {
      const filter = props.filter
      query.setFilters([filter])
    }
    if (props.orders) {
      query.setOrders(props.orders)
    }
    query.setDebug({ context: `input--EntitySelect--${props['data-debug-id']}` })
    this.collection = query.getCollection()
  }

  public async UNSAFE_componentWillReceiveProps(nextProps) {
    const clearOptionsAndMaybeReload = () => {
      this.loadingPromise?.cancel()
      this.loadingPromise = undefined

      this.setState({
        options: [],
      })
      this.isOptionsLoaded = false

      if (this.props.preloadOptions) {
        _.delay(() => this.handleInputChangedOrig(null), 10)
      }
    }

    if (!_.isEqual(this.props.filter, nextProps.filter)) {
      clearOptionsAndMaybeReload()
    }
    if (!_.isEqual(this.props.filters, nextProps.filters)) {
      clearOptionsAndMaybeReload()
    }

    if (!_.isEqual(this.props.value, nextProps.value)) {
      const { edgeToOptionCreator, value, shouldPreloadEntity, multi } = nextProps
      if (multi) {
        const edges = _.castArray(value).filter(Boolean)
        this.setState({
          value: edges.map((edge) => edgeToOptionCreator(edge, this.entitySchema)).filter(Boolean),
        })
      } else if (value?.entityId) {
        this.store.findRecord(value.entityId).then((entity: Entity) => {
          if (shouldPreloadEntity) {
            this.handleChange(entity.uniqueId, entity)
          }
          this.setState({
            entity,
            value: edgeToOptionCreator(value, this.entitySchema),
          })
        })
      } else if (value?.uniqueId) {
        this.setState({
          entity: value,
          value: value,
        })
      } else {
        this.setState({ entity: undefined, value: undefined })
      }
    }

    const resetFn = () => {
      if (this.props.runQueryOnPropsUpdate) {
        this.handleInputChangedOrig(null)
      }
    }
    if (!_.isEqual(this.props.entityType, nextProps.entityType)) {
      this.initializeCollection(nextProps).then(resetFn)
    } else {
      resetFn()
    }
  }

  public componentWillUnmount() {
    this.collection?.cancel()
    this.collection?.dispose()
    this.loadingPromise?.cancel()
  }

  public focus() {
    this.input.focus()
  }

  private get isClickable() {
    return this.props.openEntityDetailsOnClick ?? this.props.showLinkIcon
  }

  public render() {
    return this.renderSelect(null)
  }

  private renderSelect(children: React.ReactNode) {
    const { className, optionValuePath, optionRenderer, valueRenderer, multi } = this.props
    const { isLoading, menuIsOpen } = this.state
    const value = this.state.value
    const isCreatable = this._isCreatableMeta ?? this.props.isCreatable
    const optionsWithValue = this.getOptions()
    const creatableProps = {
      isCreatable,
      onNewOptionClick: this.handleNewOptionClick,
    }
    const handleRef = (ref) => {
      this.input = ref
    }

    const selectProps = {
      ...this.props
    }
    if (this.props.queryPath === ALL) {
      selectProps.filterOption = () => true
    }

    return (
      <Select
        {...selectProps}
        {...creatableProps}
        className={classNames('flex flex-row', className)}
        isAsync={true}
        multiMode={multi ? 'checkbox' : undefined}
        isLoading={isLoading}
        isHiddenOptionFn={this.isHiddenOptionFn}
        onChange={this.handleChange}
        onInputChange={this.handleInputChanged}
        onMenuClose={this.handleMenuClosed}
        menuIsOpen={menuIsOpen}
        openMenuOnClick={false}
        openMenuOnFocus={false}
        options={optionsWithValue}
        ref={handleRef}
        value={multi
          ? (_.isArray(value) ? value : [])
          : _.get(value, optionValuePath ?? '', '')}
        valueRenderer={valueRenderer ?? this.renderValue}
        optionRenderer={optionRenderer ?? this.renderOption}
        filterByFieldPaths={this.props.queryPaths?.map((p) => (typeof p === 'string' ? p : p.path))}
        components={this.reactSelectOverrides.components}
        styles={this.reactSelectOverrides.styles}
        closeMenuOnSelect={multi ? false : undefined}
        hideSelectedOptions={multi ? false : undefined}
        allowSelectAll={multi ? this.props.allowSelectAll : undefined}
        groupOptionsFn={this.props.groupOptionsFn}
        groupOptionsFormula={this.props.groupOptionsFormula}
      >
        {children}
      </Select>
    )
  }

  private renderOption = (option) => {
    const { multi } = this.props

    const optionContent = this.resolvedOptionValueCardSchema ? (
      React.createElement(
        ComponentsMap[_.get(this.resolvedOptionValueCardSchema, 'schema.type')],
        {
          entity: option.data,
          isClickable: false,
          onClick: undefined,
          isListOption: true,
          frames: this.props.frames,
          ...this.resolvedOptionValueCardSchema.schema,
        }
      )
    ) : (
      <EntityPreviewCard isListOption={true} entity={option.data} isClickable={false} onClick={undefined} frames={this.props.frames} />
    )

    if (multi) {
      return (
        <components.Option {...option}>
          <div className="c-selectCheckboxOption">
            <input
              type="checkbox"
              checked={option.isSelected}
              readOnly
              className="c-selectCheckboxOption__checkbox"
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.stopPropagation()
                  return
                }
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  option.selectOption(option.data)
                }
              }}
            />
            <div
              className="c-selectCheckboxOption__content"
              ref={(el) => { if (el) (el as any).inert = true }}
            >
              {optionContent}
            </div>
          </div>
        </components.Option>
      )
    }

    return <components.Option {...option}>{optionContent}</components.Option>
  }

  private renderValue = (props) => {
    const { isMulti } = props.selectProps
    const { entity } = this.state

    if (isMulti) {
      return this.renderMultiValueLabel(props)
    }

    const itemEntity = entity

    const content = this.resolvedSelectedValueCardSchema ? (
      React.createElement(
        ComponentsMap[_.get(this.resolvedSelectedValueCardSchema, 'schema.type')],
        {
          entity: itemEntity,
          onClick: this.isClickable ? this.handleSelectedValueClick : undefined,
          onTouchStart: this.isClickable ? this.handleSelectedValueClick : undefined,
          isClickable: this.isClickable,
          className: 'u-noBorder h-100',
          frames: this.props.frames,
          ...this.resolvedSelectedValueCardSchema.schema,
        }
      )
    ) : (
      <EntityPreviewCard
        className="u-noBorder h-100"
        entity={itemEntity}
        onClick={this.isClickable ? this.handleSelectedValueClick : undefined}
        isClickable={this.isClickable}
        frames={this.props.frames}
      />
    )

    return <components.SingleValue {...props}>{content}</components.SingleValue>
  }

  private renderMultiValueLabel = (props: any) => {
    const { labelFormula, optionLabelPath, isItemClearable } = this.props
    const option = props.data
    const settings = apis.getSettings()

    const displayName = labelFormula
      ? evaluateExpression({ settings, ...CustomFormulas, option }, labelFormula)
      : (_.get(option, optionLabelPath ?? '') ?? option.displayName)

    const canLink = this.isClickable && !!option?.uniqueId

    return (
      <components.MultiValue {...props}>
        <components.MultiValueLabel
          {...props}
          innerProps={{
            ...props.innerProps,
            style: {
              ...props.innerProps?.style,
              cursor: canLink ? 'pointer' : 'default',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            },
            onMouseDown: canLink
              ? (e: React.MouseEvent) => {
                  if (e.button !== 0) return
                  e.preventDefault()
                  e.stopPropagation()
                  this.handlePillClick(option)
                }
              : undefined,
          }}
        >
          {displayName}
        </components.MultiValueLabel>
      </components.MultiValue>
    )
  }

  private getOptionMatches(option: unknown) {
    const { queryPath, queryPaths, optionLabelPath, isHiddenOptionsFormula } = this.props
    const settings = apis.getSettings()

    const allPotentialMatches = [
      _.get(option, optionLabelPath ?? ''),
      _.get(option, queryPath ?? '')
    ]

    queryPaths?.forEach(path => {
      const pathString = _.isObject(path) ? path['path'] : path
      allPotentialMatches.push(_.get(option, pathString))
    })

    const extraHiddenOptions = _.isNil(isHiddenOptionsFormula) ? [] : evaluateExpression({
      settings,
      ...CustomFormulas,
      option,
    }, isHiddenOptionsFormula)
    allPotentialMatches.push(...extraHiddenOptions)

    return new Set(allPotentialMatches.filter(_.negate(_.isNil)))
  }

  private isHiddenOptionFn = (option, inputValue) => {
    const { isHiddenFormula, entity } = this.props
    const settings = apis.getSettings()

    if (!isHiddenFormula) {
      return false
    }

    return evaluateExpression({
      settings,
      ...CustomFormulas,
      option,
      optionMatchSet: this.getOptionMatches(option),
      inputValue,
      entity,
    }, isHiddenFormula)
  }

  private getOptions() {
    const { optionValuePath, multi } = this.props
    const { inputValue, options } = this.state
    const value = this.state.value

    if (!multi && value && !_.isEmpty(value)) {
      const isValueInOptions = _.find(options, { [optionValuePath]: value[optionValuePath] })
      if (_.isEmpty(inputValue) && !isValueInOptions) {
        return [value].concat(options)
      }
    }

    return options
  }

  private getCollectionFilters() {
    const { filters, filter } = this.props
    const isValueless = f => VALUELESS_FILTERS.has(f)

    if (
      filters &&
      filters.every((f) => f && (isValueless(f.type) || f.value || !_.isEmpty(f.values)))
    ) {
      return filters
    } else {
      if (filter && (isValueless(filter.type) || filter.value || !_.isEmpty(filter.values))) {
        return [filter]
      }
    }

    return []
  }


  /****************************************************************************/
  // Entity Creation
  /****************************************************************************/

  private createNewEntity(entitySchema: Entity, defaultValue: unknown) {
    const { openOverlay, entityCreateSchema, entityCreateState, frames } = this.props
    const dataDebugId = this.props['data-debug-id']

    const defaultWithSource = _.merge({
      creationSource: creationSourceFromWeb(`Entity Select (${dataDebugId})`, frames)
    }, defaultValue)

    const subschema = this.schemaResolver.resolveSubschemaByPath(entitySchema, entityCreateSchema)

    if (subschema) {
      openOverlay(
        <EntityFormSheet
          defaultValue={defaultWithSource}
          entitySchema={entitySchema}
          onCreate={this.handleEntitySaved}
          onSelect={this.handleEntitySelected}
          uiSchemaPath={entityCreateSchema}
          extraState={entityCreateState}
        />
      )
    } else {
      ConfirmationModal.open(
        {
          confirmationTitle: `${_.capitalize(entitySchema.displayName)} creation unavailable`,
          confirmationText: `${entitySchema.displayName} creation is not available in this context`,
          modalDialogClassName: 'c-modal-dialog--sm',
          cancelButtonText: 'Close',
        }
      )
    }
  }

  private createNewAssociateNewEntity(newOptionAssociationProps, entityEdge, defaultValue) {
    const { openOverlay } = this.props
    const { associatedEntityType, edgePath, entityType, isEdgeOnEntity } = newOptionAssociationProps
    const associatedEntitySchema = this.store.getRecord(associatedEntityType)
    const entitySchema = this.store.getRecord(entityType)
    this.store.findRecord(entityEdge.entityId).then((entity: Entity) => {
      openOverlay(
        <EntityAssociationsSheet
          associatedEntityDefaultValue={defaultValue}
          associatedEntitySchema={associatedEntitySchema}
          entity={entity}
          entitySchema={entitySchema}
          edgePath={edgePath}
          isEdgeOnEntity={isEdgeOnEntity}
          onChange={this.handleEntitySaved}
        />
      )
    })
  }

  /****************************************************************************/
  // Handlers
  /****************************************************************************/

  // tslint:disable-next-line:member-ordering
  public handleChange = async (entityId, option, silentUpdate = false, overridePath = null, actionMeta = null) => {
    const {
      entityToEdgeCreator,
      denormalizedProperties = [],
      returnValueAsEdge,
      openEntityDetailsOnChange,
      onChange,
      multi,
      confirmChangeFn,
    } = this.props
    const { entity } = this.state

    if (multi) {
      const finalOptions: any[] = option || []
      const edges = finalOptions.map((opt) =>
        returnValueAsEdge ? entityToEdgeCreator?.(opt, this.entitySchema, denormalizedProperties) : opt
      )
      onChange?.(edges, finalOptions)
      this.setState({ value: finalOptions })
      return
    }

    if (entityId) {
      const newValue = !returnValueAsEdge
        ? option
        : entityToEdgeCreator?.(option, this.entitySchema, denormalizedProperties)

      const confirmChange = (await confirmChangeFn?.(entity, option)) ?? true
      if (!confirmChange) {
        return
      }
      onChange?.(newValue, option)
      this.setState({ entity: option }, () => {
        const userActions = ['select-option']
        if (openEntityDetailsOnChange && userActions.includes(actionMeta?.action)) {
          this.handleSelectedValueClick()
        }
      })
    } else {
      onChange?.(undefined)
    }
  }

  private handleEntitySelected = (entity: Entity) => {
    const { entityToEdgeCreator, denormalizedProperties = [], onChange, returnValueAsEdge } = this.props

    const value = returnValueAsEdge
      ? entityToEdgeCreator?.(entity, this.entitySchema, denormalizedProperties)
      : entity
    onChange?.(value, entity)
    this.setState({ entity: entity, value: value })
  }

  private handleEntitySaved = (entity: Entity) => {
    const { entityToEdgeCreator, denormalizedProperties = [], returnValueAsEdge, onChange, multi } = this.props

    // It takes some time for some newly created entities to have their denormalized properties inflated.
    // We notify the parent entity that an inflation is pending so as to prevent saving until inflation is finished.
    const inflationSessionId = uuidv4()
    this.props.addInflationSessionId?.(inflationSessionId)

    entity
      .waitUntilIdle()
      .then(() => {
        if (multi) {
          if (this.state.menuIsOpen) {
            // Menu is open: add to the in-progress draft in react-select, then also
            // call onChange immediately so the parent reflects the new entity.
            this.input.updateDraft((draft) => [...draft, entity])
            const currentValue = _.isArray(this.state.value) ? this.state.value : []
            const newOptions = [...currentValue, entity]
            const edges = newOptions.map((opt) =>
              returnValueAsEdge ? entityToEdgeCreator?.(opt, this.entitySchema, denormalizedProperties) : opt
            )
            onChange?.(edges, newOptions)
          } else {
            const currentValue = _.isArray(this.state.value) ? this.state.value : []
            const newOptions = [...currentValue, entity]
            const edges = newOptions.map((opt) =>
              returnValueAsEdge ? entityToEdgeCreator?.(opt, this.entitySchema, denormalizedProperties) : opt
            )
            onChange?.(edges, newOptions)
            this.setState({ value: newOptions })
          }
        } else {
          const value = entityToEdgeCreator?.(entity, this.entitySchema, denormalizedProperties)
          onChange?.(value, entity)
        }
      })
      .finally(() => {
        this.props.removeInflationSessionId?.(inflationSessionId)
        this.setState({ entity: entity })
      })
  }

  private handlePillClick = (option: any) => {
    const { openOverlay, entityEditSchema, entityEditState, entityDetailSchema, entityDetailState, entitySheetMode } = this.props

    if (!this.isClickable || !option?.uniqueId) {
      return
    }

    const uiSchemaPath = entitySheetMode === EntityFormSheetMode.Edit ? entityEditSchema : entityDetailSchema
    const uiState = entitySheetMode === EntityFormSheetMode.Edit ? entityEditState : entityDetailState
    const subschema = this.schemaResolver.resolveSubschemaByPath(this.entitySchema, uiSchemaPath)

    this.store.findRecord(option.uniqueId).then((entity: Entity) => {
      if (subschema) {
        openOverlay(
          <EntityFormSheet
            entity={entity}
            onSelect={(updated: Entity) => this.handlePillEntityUpdate(option, updated)}
            onEdit={(updated: Entity) => this.handlePillEntityUpdate(option, updated)}
            uiSchemaPath={uiSchemaPath}
            extraState={uiState}
            mode={entitySheetMode}
          />
        )
      } else {
        window.open(`/entity/${entity.uniqueId}`, '_blank')
      }
    })
  }

  private handlePillEntityUpdate = (originalOption: any, entity: Entity) => {
    const { entityToEdgeCreator, denormalizedProperties = [], returnValueAsEdge, onChange, optionValuePath = ''} = this.props

    const inflationSessionId = uuidv4()
    this.props.addInflationSessionId?.(inflationSessionId)

    entity
      .waitUntilIdle()
      .then(() => {
        const replaceInArray = (arr: any[]) =>
          arr.map((opt) =>
            _.get(opt, optionValuePath) === _.get(originalOption, optionValuePath) ? entity : opt
          )

        if (this.state.menuIsOpen) {
          // Menu is open: update the in-progress draft in react-select, then also call
          // onChange so the parent reflects the updated entity.
          this.input.updateDraft((draft) => replaceInArray(draft))
          const currentValue = _.isArray(this.state.value) ? this.state.value : []
          const newOptions = replaceInArray(currentValue)
          const edges = newOptions.map((opt) =>
            returnValueAsEdge ? entityToEdgeCreator?.(opt, this.entitySchema, denormalizedProperties) : opt
          )
          onChange?.(edges, newOptions)
        } else {
          const currentValue = _.isArray(this.state.value) ? this.state.value : []
          const newOptions = replaceInArray(currentValue)
          const edges = newOptions.map((opt) =>
            returnValueAsEdge ? entityToEdgeCreator?.(opt, this.entitySchema, denormalizedProperties) : opt
          )
          onChange?.(edges, newOptions)
          this.setState({ value: newOptions })
        }
      })
      .finally(() => {
        this.props.removeInflationSessionId?.(inflationSessionId)
      })
  }

  private handleMenuOpened = () => {
    const { options } = this.state

    if (_.isEmpty(options)) {
      this.handleInputChangedOrig(null)
    }

    this.setState({ menuIsOpen: true }, () => {
      if (!this.props.multi) {
        this.input?.focus()
      }
    })
  }

  private handleMenuClosed = () => {
    this.setState({ menuIsOpen: false })
  }

  private handleInputChanged = async (input: string | null) => {
    // created by debounce above
  }

  private handleInputChangedOrig = async (input: string | null) => {
    this.loadingPromise?.cancel()

    this.loadingPromise = this._handleInputChanged(input)
    return this.loadingPromise
  }

  private _handleInputChanged = async (input) => {
    input = input ? input.trim() : null

    if (this.isOptionsLoaded && input === this.state.inputValue) {
      return
    }

    this.collection?.cancel()
    this.setState({ isLoading: true })

    if (isUUIDValid(input)) {
      return apis.getStore().findRecord(input).then((record: Entity) => {
        this.setState(
          {
            entity: record,
            inputValue: record.displayName,
            isLoading: false,
            options: [record],
          }
        )
        this.isOptionsLoaded = true
      }).catch(() => {
        this.setState({
          isLoading: false,
          options: [],
        })
        this.isOptionsLoaded = true
      })
    }

    const queryInput = this.props.transformQueryFn?.(input) ?? input

    this.collection.query.setQuery(queryInput)
    this.collection.query.setFilters(this.getCollectionFilters())
    return this.collection.find().then((entities) => {
      const newState: Partial<IEntitySelectState> = {
        inputValue: input,
        isLoading: false,
        options: entities,
      }

      if (this.props.multi && !_.isEmpty(input) && !this.state.menuIsOpen) {
        newState.menuIsOpen = true
      }

      this.setState(newState as any)
      this.isOptionsLoaded = true

      const { onOptionsChange } = this.props
      onOptionsChange?.({ options: entities })
    })
  }

  private handleNewOptionClick = async (value: string) => {
    const {
      entityType,
      filter,
      newOptionAssociationProps,
      onNewOptionClick,
      defaultNewEntityValue,
      defaultNewEntityInputValuePath,
    } = this.props
    if (onNewOptionClick) {
      return onNewOptionClick(value)
    }

    const entitySchema = this.store.getRecord(entityType)
    const recordTemplate = this.store.createRecord(entitySchema)
    const defaultValueFromTemplate = getNewEntityDefaultValue(
      recordTemplate,
      value,
      defaultNewEntityValue,
      defaultNewEntityInputValuePath
    )
    const defaultValue = await this.processMappings(defaultValueFromTemplate)

    if (newOptionAssociationProps && filter?.value) {
      const entityEdge = filter.value
      this.createNewAssociateNewEntity(newOptionAssociationProps, entityEdge, defaultValue)
    } else {
      this.createNewEntity(entitySchema, defaultValue)
    }
  }

  private async processMappings(defaultValue) {
    defaultValue = _.defaultTo(defaultValue, {})

    const { entity, entityType, newOptionMappings } = this.props
    if (_.isEmpty(newOptionMappings)) {
      return defaultValue
    }

    const iMappings = toIMapping(entity, newOptionMappings, defaultValue)
    const settings = entity?.getStore()?.api?.settings || {}
    const schemaId = entity?.getStore()?.getRecord(entityType)?.uniqueId
    await EntityMapper.create(apis, settings)
      .fromEntity(entity)
      .useMappings(iMappings)
      .to(defaultValue, [schemaId])
      .execute({
        settings,
      })

    return defaultValue
  }

  private handleSelectedValueClick = () => {
    const {
      openOverlay,
      entityEditSchema,
      entityEditState,
      entityDetailSchema,
      entityDetailState,
      entitySheetMode,
    } = this.props
    const { entity } = this.state

    if (!this.isClickable) {
      return
    }

    const uiSchemaPath = entitySheetMode === EntityFormSheetMode.Edit ? entityEditSchema : entityDetailSchema
    const uiState = entitySheetMode === EntityFormSheetMode.Edit ? entityEditState : entityDetailState
    const subschema = this.schemaResolver.resolveSubschemaByPath(this.entitySchema, uiSchemaPath)

    if (subschema) {
      openOverlay(
        <EntityFormSheet
          entity={entity}
          onSelect={this.handleEntitySelected}
          onEdit={this.handleEntitySaved}
          uiSchemaPath={uiSchemaPath}
          extraState={uiState}
          mode={entitySheetMode}
        />
      )
    } else {
      window.open(`/entity/${entity.uniqueId}`, '_blank')
    }
  }

  private reactSelectOverrides: { styles: SelectStyleProps; components: SelectComponentProps } = {
    styles: {
      valueContainer: (base) => ({
        ...base,
        alignItems: 'stretch',
      }),
      singleValue: (base) => ({
        ...base,
        transform: 'unset',
        position: 'relative',
        top: 'unset',
        flex: 1,
        whiteSpace: 'unset',
        maxWidth: 'unset',
        cursor: this.isClickable ? 'pointer' : 'default',
        alignItems: 'stretch',
        margin: '5px -3px',
        borderRadius: '5px',
        border: '1px solid #e0e0e0',
        boxShadow: this.isClickable ? '0 1px 2px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.06);' : 'none',
        backgroundColor: '#f5f5f5',
      }),
      container: (base) => {
        const { isDisabled } = this.props
        return {
          ...base,
          pointerEvents: isDisabled && !this.isClickable ? 'none' : 'auto',
        }
      },
      clearIndicator: (base) => ({
        ...base,
        height: '100%',
        alignItems: 'center',
        cursor: 'pointer',
      }),
      control: (base) => ({
        ...base,
        justifyContent: 'flex-start',
        alignItems: 'stretch',
        marginBottom: 0,
      }),
      dropdownIndicator: (base) => ({
        ...base,
        height: '100%',
        alignItems: 'center'
      })
    },
    components: {
      ValueContainer: (props: any) => {
        const { isDisabled, innerProps } = props
        const onMouseDown = (e: any) => {
          const allowOpen = this.props.multi
            ? !props.selectProps.menuIsOpen && !isDisabled
            : !props.selectProps.menuIsOpen && !isDisabled && !props.selectProps.value
          if (allowOpen) {
            e.preventDefault()
            e.stopPropagation()
            this.handleMenuOpened()
          }
        }

        const onTouchEnd = (e: any) => {
          e.preventDefault()
          e.stopPropagation()
        }

        return (
          <components.ValueContainer
            {...props}
            innerProps={{ ...innerProps, onMouseDown, onTouchStart: onMouseDown, onTouchEnd }}
          />
        )
      },
      MultiValueRemove: (props: any) => {
        const { isItemClearable } = this.props
        if (isItemClearable === false || props.selectProps.isDisabled) {
          return null
        }
        return <components.MultiValueRemove {...props} />
      },
      DropdownIndicator: (props) => {
        const { isDisabled, innerProps } = props
        if (isDisabled) {
          return null
        }

        const onMouseDown = (e: any) => {
          if (!props.selectProps.menuIsOpen) {
            e?.preventDefault()
            e?.stopPropagation()
            this.handleMenuOpened()
          }
        }

        const onTouchEnd = (e: any) => {
          e.preventDefault()
          e.stopPropagation()
        }

        return (
          <components.DropdownIndicator
            {...props}
            innerProps={{ ...innerProps, onMouseDown, onTouchStart: onMouseDown, onTouchEnd }}
          />
        )
      },
    },
  }
}

// TODO(Peter): we should generalize this and move this logic into the schemas
export function getNewEntityDefaultValue(
  entity,
  newOptionString,
  defaultValue = null,
  defaultInputValuePath = null
) {
  const schemaIds = _.map(entity.schemas, 'id')
  if (defaultValue == null) {
    if (_.includes(schemaIds, '/1.0/entities/metadata/business.json')) {
      return {
        business: { legalName: newOptionString },
      }
    } else if (_.includes(schemaIds, '/1.0/entities/metadata/location.json')) {
      return {
        location: { name: newOptionString },
      }
    } else if (_.includes(schemaIds, '/1.0/entities/metadata/person.json')) {
      const tokens = newOptionString.split(/\s+/)
      return {
        person: {
          firstName: tokens[0],
          lastName: tokens[1],
        },
      }
    } else if (_.includes(schemaIds, '/1.0/entities/metadata/core_yms_trailer.json')) {
      return {
        core_yms_trailer: {
          name: newOptionString,
        },
      }
    }
  }
  if (defaultInputValuePath != null) {
    defaultValue = _.set(defaultValue, defaultInputValuePath, newOptionString)
  }
  return defaultValue
}

function defaultEntityToEdgeCreator(entity, schema, edgeDenormalizedProperties) {
  const properties = _.get(schema, 'metadata.denormalizedProperties') || []
  const allDenormalizedProps = _.uniq<string>(properties.concat(edgeDenormalizedProperties || []))

  const denormalizedProperties: Record<string, unknown> = {}
  _.forEach(allDenormalizedProps, (path) => {
    denormalizedProperties[path] = _.get(entity, path)
  })

  return {
    denormalizedProperties,
    displayName: entity.displayName,
    entityId: entity.uniqueId,
  }
}

function defaultEdgeToOptionCreator(edge) {
  if (_.isNil(edge?.entityId) || _.isNil(edge?.displayName)) {
    return
  }
  const option = {
    displayName: edge.displayName,
    uniqueId: edge.entityId,
  }
  const properties = _.get(edge, 'denormalizedProperties')
  _.forEach(properties, (value, path) => _.set(option, path, value))
  return option
}

export default React.forwardRef((props: IEntitySelectProps, ref: React.Ref<EntitySelect>) => (
  <SheetContext.Consumer>
    {({ openOverlay }) => <EntitySelect {...props} openOverlay={openOverlay} ref={ref} />}
  </SheetContext.Consumer>
))
